import React from 'react';
import { Button, Container } from 'react-bootstrap';
import { useAuth0 } from '@auth0/auth0-react';
import { performHardLogout } from '@/lib/authLogout';
import styles from './super-admin-access-denied.module.scss';

export type SuperAdminAccessDeniedReason = 'pending' | 'denied' | 'unavailable' | 'error';

interface SuperAdminAccessDeniedProps {
  reason: SuperAdminAccessDeniedReason;
  detailMessage?: string;
  onRetry?: () => void;
}

const SuperAdminAccessDenied: React.FC<SuperAdminAccessDeniedProps> = ({
  reason,
  detailMessage,
  onRetry,
}) => {
  const { logout, user } = useAuth0();

  const handleSignOut = React.useCallback(() => {
    void performHardLogout(logout);
  }, [logout]);

  const title =
    reason === 'pending'
      ? 'Access pending approval'
      : reason === 'unavailable'
        ? 'Access verification unavailable'
        : reason === 'error'
          ? 'Could not verify access'
          : 'Access denied';

  const body =
    reason === 'pending'
      ? 'Your account has successfully authenticated through our identity provider, but your KrossKast Super Admin access is still pending approval. A Super Admin must authorize and activate your account before you can access this application.'
      : reason === 'unavailable'
        ? 'The server could not run the Super Admin access check. You cannot enter the application until this is resolved. If you believe you should have access, contact your administrator.'
        : reason === 'error'
          ? detailMessage ||
            'We could not confirm your Super Admin access. Please try again or sign out.'
          : 'You do not have permission to use the Super Admin application.';

  return (
    <Container className={styles.page} fluid>
      <div className={styles.card}>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.body}>{body}</p>
        {user?.email ? (
          <p className={styles.email}>
            Signed in as <strong>{user.email}</strong>
          </p>
        ) : null}
        <div className={styles.actions}>
          {onRetry ? (
            <Button type="button" variant="outline-primary" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
          <Button type="button" variant="primary" onClick={handleSignOut}>
            Sign out
          </Button>
        </div>
      </div>
    </Container>
  );
};

export default SuperAdminAccessDenied;
